import React, { ReactNode, useCallback, useState } from "react";
import classnames from "classnames";
import equal from "fast-deep-equal";

import { groupBy } from "@formant/core/common/groupBy";

import { Escape } from "../Escape";
import { IconButton } from "../IconButton";
import { WindowEvent } from "../WindowEvent";
import { IMenuItem } from "./IMenuItem";
import styles from "./index.scss";
import { MenuItem } from "./MenuItem";
import { MenuItemGroup } from "./MenuItemGroup";

export interface IMenuProps<T = string> {
    visible?: boolean;
    title?: string;
    titleExtra?: ReactNode;
    items: IMenuItem<T>[];
    bottom?: boolean;
    right?: boolean;
    className?: string;
    autofocus?: boolean;
    closeOnSelect?: boolean;
    style?: "black" | "blue";
    onSelected?: (value: T) => void;
    onClose: () => void;
    tooltip?: string;
    legacy?: boolean;
    preventCloseOnBlur?: boolean;
}

function focus(element: HTMLElement | null) {
    element?.focus();
}

export const Menu = function Menu<T>(props: IMenuProps<T>) {
    const [hovered, setHovered] = useState<T | undefined>(undefined);

    const {
        autofocus = true,
        bottom = false,
        className,
        items,
        legacy = true,
        onClose,
        right = false,
        style = "black",
        title,
        titleExtra,
        tooltip,
        onSelected,
        closeOnSelect = true,
        visible,
        preventCloseOnBlur = false,
    } = props;

    const onHover = useCallback(
        (value: T) => {
            setHovered(value);
        },
        [setHovered],
    );

    const handleSelected = useCallback(
        async (value: T) => {
            onSelected?.(value);
            items.find(_ => equal(_.value, value))?.onSelected?.(value);
            if (closeOnSelect) {
                onClose();
            }
        },
        [items, onSelected, closeOnSelect, onClose, legacy],
    );

    const onKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (!visible) {
                return;
            }

            const { key } = event;

            const hoveredIndex =
                (key === "ArrowDown" || key === "ArrowUp"
                    ? items.findIndex(_ => equal(_.value, hovered))
                    : undefined) ?? -1;

            if (key === "ArrowDown") {
                event.preventDefault();

                const next = (hoveredIndex + 1) % items.length;
                setHovered(items[next]?.value);
            } else if (key === "ArrowUp") {
                event.preventDefault();

                const count = items.length;
                const next = (hoveredIndex + count - 1) % count;
                setHovered(items[next]?.value);
            } else if ((key === "Enter" || key === "Tab") && hovered) {
                event.preventDefault();
                if (hovered) {
                    void handleSelected(hovered);
                }
            } else {
                setHovered(undefined);
            }
        },
        [items, visible, hovered, handleSelected],
    );

    const groups = items.some(_ => _.group)
        ? [...groupBy(items, ({ group }) => group || 0).entries()].map(
              _ => _[1],
          )
        : [items];

    const children = groups.map((group, index) => (
        <MenuItemGroup key={index} className={styles.group}>
            {group.map(_ => (
                <MenuItem
                    key={
                        _.value !== undefined
                            ? JSON.stringify(_.value)
                            : "undefined"
                    }
                    className={styles.item}
                    {..._}
                    hovered={equal(_.value, hovered)}
                    onHover={onHover}
                    onSelected={handleSelected}
                    data-testid={`menu-item-${_.value}`}
                />
            ))}
        </MenuItemGroup>
    ));

    return (
        <div
            className={classnames(
                styles.menu,
                legacy && styles.legacy,
                visible && styles.visible,
                bottom && styles.bottom,
                right && styles.right,
                style && styles[style],
                className,
            )}
        >
            <WindowEvent type="keydown" onEvent={onKeyDown} />
            <Escape onEsc={onClose} />
            <div
                className={styles.content}
                key={visible ? 1 : 0}
                ref={autofocus ? focus : undefined}
                tabIndex={0}
                onBlur={preventCloseOnBlur ? undefined : onClose}
                data-tooltip={tooltip}
            >
                {title && (
                    <div className={styles.title}>
                        {title}
                        <div className={styles.extra}>{titleExtra}</div>
                    </div>
                )}
                <IconButton
                    icon="close"
                    className={styles.close}
                    onClick={onClose}
                />
                <div className={styles.groups}>{children}</div>
            </div>
        </div>
    );
};
